import { X, ExternalLink, Star } from "lucide-react";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const assetBase = `${process.env.PUBLIC_URL || ""}/game-assets`;

// Proof-of-play discount popup: shows the NicaBros logo, the partner brand and a
// big, high-contrast discount code the player shows to a server / cashier.
export default function PartnerCodePopup({ billboard, onClose }) {
  const { t } = useLang();
  if (!billboard) return null;
  const b = billboard;
  const visit = () => {
    api.trackHomeBillboardClick(b.id);
    if (b.link) window.open(b.link, "_blank", "noopener,noreferrer");
  };
  return (
    <div className="fixed inset-0 z-[10070] bg-black/90 overflow-y-auto overscroll-contain flex items-start justify-center p-4 scanlines"
      onClick={onClose} data-testid="partner-code-popup">
      <div className="pixel-card bg-[#141414] w-full max-w-sm my-auto max-h-full text-center pop-in p-6 relative"
        onClick={(e) => e.stopPropagation()} data-testid="partner-code-card">
        <button data-testid="partner-code-close" onClick={onClose}
          className="pixel-btn bg-black/55 text-white p-2 absolute top-3 right-3"><X size={16} strokeWidth={2.5} /></button>

        <img src={`${assetBase}/nicabros-logo.webp`} alt="NicaBros" className="h-12 w-auto mx-auto mb-3 drop-shadow-[0_4px_10px_rgba(0,0,0,0.6)]"
          onError={(e) => { e.target.style.display = "none"; }} />

        <p className="font-pixel text-[10px] text-[#FFEB3B] pixel-stroke flex items-center justify-center gap-1.5 leading-relaxed px-2">
          <Star size={14} strokeWidth={2.5} className="fill-[#FFEB3B] shrink-0" /> {t("partnerPopupTitle")}
        </p>

        {(b.has_image || b.has_video) && (
          <div className="mt-3 rounded-xl overflow-hidden border-2 border-[#FFB300]/40 bg-black aspect-video">
            {b.has_video && b.video_url
              ? <video src={`${BACKEND}${b.video_url}`} className="w-full h-full object-cover" muted loop autoPlay playsInline />
              : <img src={`${BACKEND}${b.image_url}`} alt={b.label || "partner"} className="w-full h-full object-cover" draggable={false} />}
          </div>
        )}

        {b.label && <p className="font-vt text-lg text-white/85 mt-3" data-testid="partner-code-brand">{b.label}</p>}

        <div className="mt-2 rounded-2xl bg-[#FFD400] ring-4 ring-white/90 px-4 py-4 shadow-[0_8px_24px_rgba(0,0,0,0.55)]">
          <p className="font-pixel text-[10px] text-black/70">{t("partnerCodeLabel")}</p>
          <p className="font-pixel text-2xl md:text-3xl text-black tracking-widest break-all mt-1" data-testid="partner-code-value">
            {b.discount_code}
          </p>
        </div>

        <p className="font-vt text-base text-white/70 mt-3 leading-snug px-1">
          {b.label ? t("partnerPopupBody")(b.label) : t("partnerPopupBodyGeneric")}
        </p>

        {b.link && (
          <button data-testid="partner-code-visit" onClick={visit}
            className="pixel-btn bg-[#0067C6] text-white w-full py-3 text-xs mt-4 flex items-center justify-center gap-2">
            <ExternalLink size={16} strokeWidth={2.5} /> {b.cta || t("partnerVisit")}
          </button>
        )}
      </div>
    </div>
  );
}
